import { io } from 'socket.io-client';
import { getActiveSubscription, getActiveUser } from '@/data/session/session-globals';
import { http } from '@pendo/http';
import { i18n_t as $t } from '@/utils/i18n';
import { queueMessage } from '@/common/use/use-aria-live-registry/use-aria-live-registry';

export const WS_EVENT_TYPES = {
    THREADS_BY_DOCUMENT_ID: 'threadsByDocumentId',
    NEW_THREAD: 'newThread',
    DELETED_THREAD: 'deletedThread',
    NEW_COMMENT: 'newComment',
    DELETED_COMMENT: 'deletedComment',
    EDITED_COMMENT: 'editedComment',
    NEW_EMOJI_REACTION: 'newEmojiReaction',
    DELETED_EMOJI_REACTION: 'deletedEmojiReaction',
};

const DOCUMENT_ID_DELIMITER = '_:';

const validCommentKinds = [
    'dashboard',
    'replay',
    'crosstabreplay',
    'savedclip',
    'idea',
    'feedback',
    'space',
];

// Holds a map of documentIds and each callback registered to the documentId
const documentIdCallbacks = new Map();
let socket;

// connect to server
export function connectToWebSocketServer ({ dispatch }) {
    if (socket) return;

    const serverUrl = `wss://${window.location.host}/in-app-commenting`;
    const opts = {
        path: '/ws2/socket.io',
        transports: ['websocket'],
        forceNew: false,
        autoConnect: false,
    };
    socket = io(serverUrl, opts);
    socket.connect();

    socket.on('connect', () => {
        dispatch('pendoCollaboration/init', null, { root: true });

        // Rejoin rooms on reconnection
        if (documentIdCallbacks.size > 0) {
            const subId = getActiveSubscription()?.id;
            for (const documentId of documentIdCallbacks.keys()) {
                socket.emit('subscribeToDocument', { subId, documentId });
            }
        }
    });

    socket.on('connect_error', () => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
    });

    socket.on('disconnect', (reason) => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
        // socket.io won't auto-reconnect for these reasons
        if (reason === 'io server disconnect' || reason === 'io client disconnect') {
            socket = null;
            documentIdCallbacks.clear();
        }
        // For transport errors/timeouts, socket.io reconnects automatically
        // and the 'connect' handler will rejoin rooms
    });

    socket.on('message', ({ type, documentId, data }) => {
        documentIdCallbacks.get(documentId)?.forEach((cb) => cb({ type, data }));
    });
}

function formatAuthorName (user) {
    const { first, last, username } = user || {};
    if (first || last) return [first, last].filter(Boolean).join(' ');

    return username || '';
}

export async function subscribeToDocument ({ documentId, callback }) {
    if (!socket) return () => {};

    // Join the room if not already subscribed
    if (!documentIdCallbacks.has(documentId)) {
        documentIdCallbacks.set(documentId, new Set());
        const subId = getActiveSubscription()?.id;
        await synchronousSocketCall(socket, 'subscribeToDocument', { subId, documentId });
    }

    if(callback) {
        documentIdCallbacks.get(documentId).add(callback);
    }

    // Return unsubscribe function
    return function unsubscribe () {
        const callbacks = documentIdCallbacks.get(documentId);
        if (!callbacks) return;
        callbacks.delete(callback);
        if (callbacks.size === 0) {
            documentIdCallbacks.delete(documentId);
            socket?.emit('leaveRooms', { documentId });
        }
    };
}

function buildPermissionEntitiesField ({ documentId, widgetId, targetElementId, targetEntityType = 'widget' }) {
    let [ kind, ...idParts ] = documentId.split(DOCUMENT_ID_DELIMITER);
    // handles the case where the delimiter appears in the id
    const id = idParts.join(DOCUMENT_ID_DELIMITER);
    const entity = {};
    if (kind.toLowerCase() === 'crosstabreplay') {
        kind = 'replay';
    }
    if (kind.toLowerCase() === 'savedclip') {
        kind = 'saved clip';
    }
    entity[kind.toLowerCase()] = [id];
    if (widgetId) {
        entity.widget = [ widgetId ];
    } else if (targetElementId) {
        entity[targetEntityType] = [ targetElementId ];
    }

    return entity;
}

export async function createThread ({ documentId, targetElementId, targetEntityType = 'widget', textBody, mentionedUserIds, contextJson, pageUrl, pageTitle }) {
    if (!documentId) {
        console.error('Missing required field documentId');

        return;
    }
    const subId = getActiveSubscription()?.id;
    const entities = targetEntityType === 'widget'
        ? buildPermissionEntitiesField({ documentId, widgetId: targetElementId })
        : buildPermissionEntitiesField({ documentId, targetElementId, targetEntityType });
    await synchronousSocketCall(socket, 'createThread', { subId, documentId, targetElementId, textBody, mentionedUserIds, entities, contextJson, pageUrl, pageTitle });
}

export async function deleteThread ({ documentId, threadId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteThread', { subId, documentId, threadId });
}

export async function addComment ({ documentId, textBody, mentionedUserIds, threadId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'addComment', { subId, documentId, textBody, mentionedUserIds, threadId });
}

export async function deleteComment ({ documentId, threadId, commentId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteComment', { subId, documentId, threadId, commentId });
}

export async function editComment ({ documentId, textBody, mentionedUserIds, threadId, commentId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'editComment', { subId, documentId, textBody, mentionedUserIds, threadId, commentId });
}

export async function addEmojiReaction ({ documentId, threadId, commentId, emojiCode }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'addEmojiReaction', { subId, documentId, threadId, commentId, emojiCode });
}

export async function deleteEmojiReaction ({ documentId, threadId, commentId, emojiCode }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteEmojiReaction', { subId, documentId, threadId, commentId, emojiCode });
}

export async function getNotifications () {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'getAllNotifications', { subId });
}

export async function synchronousSocketCall (socket, messageName, messageData = {}) {
    if(!socket) throw new Error('Socket was not provided or is not yet initialized');

    const response = await socket.timeout(10000).emitWithAck(messageName, messageData);

    if(response.isError) {
        throw response;
    }

    return response;
}

export function generateDocumentId ({ kind, id }) {
    if (!validCommentKinds.includes(kind)) {
        console.error(`Invalid kind ${kind} provided.`);
    }
    const prefix = kind.toUpperCase();

    return `${prefix}${DOCUMENT_ID_DELIMITER}${id}`;
}

export async function markThreadAsRead ({ threadId }) {
    const { data } = await http.put(`/api/s/_SID_/in-app-commenting/thread/${threadId}/read`);

    return data;
}

export function handleCommentingSocketMessages (commentThreads, { type, data }) {
    const currentUserId = getActiveUser()?.id;

    switch (type) {
        case WS_EVENT_TYPES.THREADS_BY_DOCUMENT_ID: {
            commentThreads.clear();
            if (data) {
                data.forEach((thread) => {
                    if (thread.inAppCommentThread?.id) {
                        commentThreads.set(thread.inAppCommentThread.id, thread);
                    }
                });
            }
            break;
        }
        case WS_EVENT_TYPES.NEW_THREAD: {
            if (!data?.inAppCommentThread?.id) break;
            commentThreads.set(data.inAppCommentThread.id, data);

            const actor = data?.inAppCommentThread?.createdByUser;
            if (actor?.id === currentUserId) break;
            const author = formatAuthorName(actor);
            queueMessage({
                message: author
                    ? $t('commentThreadList.New thread from author', { author })
                    : $t('commentThreadList.New thread'),
            });
            break;
        }
        case WS_EVENT_TYPES.DELETED_THREAD: {
            if (!commentThreads.has(data?.threadId)) break;
            commentThreads.delete(data.threadId);

            const actor = data?.deletedByUser;
            if (actor?.id === currentUserId) break;
            const author = formatAuthorName(actor);
            queueMessage({
                message: author
                    ? $t('commentThreadList.Thread deleted by author', { author })
                    : $t('commentThreadList.Thread deleted'),
            });
            break;
        }
        case WS_EVENT_TYPES.NEW_COMMENT: {
            const threadToUpdate = commentThreads.get(data.inAppCommentThreadId);
            if (!threadToUpdate) break;

            commentThreads.set(data.inAppCommentThreadId, {
                ...threadToUpdate,
                comments: [
                    ...threadToUpdate.comments,
                    data,
                ].slice().sort((a, b) => a.createdAt - b.createdAt),
            });

            const actor = data?.createdByUser;
            if (actor?.id === currentUserId) break;
            const author = formatAuthorName(actor);
            queueMessage({
                message: author
                    ? $t('commentThreadList.New comment from author', { author })
                    : $t('commentThreadList.New comment'),
            });
            break;
        }
        case WS_EVENT_TYPES.EDITED_COMMENT: {
            const threadToUpdate = commentThreads.get(data.inAppCommentThreadId);
            if (!threadToUpdate) break;

            commentThreads.set(data.inAppCommentThreadId, {
                ...threadToUpdate,
                comments: threadToUpdate.comments.map((comment) => {
                    if (comment.id === data.id) {
                        return {
                            ...comment,
                            textBody: data.textBody,
                            mentionedUserIds: data.mentionedUserIds || [],
                            lastUpdatedAt: data.lastUpdatedAt ?? comment.lastUpdatedAt,
                        };
                    }

                    return comment;
                }),
            });

            const actor = data?.createdByUser;
            if (actor?.id === currentUserId) break;
            const author = formatAuthorName(actor);
            queueMessage({
                message: author
                    ? $t('commentThreadList.Comment edited by author', { author })
                    : $t('commentThreadList.Comment edited'),
            });
            break;
        }
        case WS_EVENT_TYPES.DELETED_COMMENT: {
            const threadToUpdate = commentThreads.get(data.threadId);
            if (!threadToUpdate) break;

            commentThreads.set(data.threadId, {
                ...threadToUpdate,
                comments: threadToUpdate.comments.map((comment) => {
                    if (comment.id === data.commentId) {
                        return {
                            ...comment,
                            deleted: true,
                            textBody: '',
                            mentionedUserIds: [],
                        };
                    }

                    return comment;
                }),
            });

            const actor = data?.deletedByUser;
            if (actor?.id === currentUserId) break;
            const author = formatAuthorName(actor);
            queueMessage({
                message: author
                    ? $t('commentThreadList.Comment deleted by author', { author })
                    : $t('commentThreadList.Comment deleted'),
            });
            break;
        }
        case WS_EVENT_TYPES.NEW_EMOJI_REACTION:
        case WS_EVENT_TYPES.DELETED_EMOJI_REACTION: {
            const threadToUpdate = commentThreads.get(data.inAppCommentThreadId);
            if (!threadToUpdate) break;

            commentThreads.set(data.inAppCommentThreadId, {
                ...threadToUpdate,
                comments: threadToUpdate.comments.map((comment) => {
                    if (comment.id === data.id) {
                        return {
                            ...comment,
                            reactions: data.reactions || [],
                        };
                    }

                    return comment;
                }),
            });

            const actor = data?.reactedBy;
            if (actor?.id === currentUserId) break;
            const author = formatAuthorName(actor);
            const isAdded = type === WS_EVENT_TYPES.NEW_EMOJI_REACTION;
            queueMessage({
                message: author
                    ? $t(isAdded
                        ? 'commentThreadList.Reaction added by author'
                        : 'commentThreadList.Reaction removed by author', { author })
                    : $t(isAdded
                        ? 'commentThreadList.Reaction added'
                        : 'commentThreadList.Reaction removed'),
            });
            break;
        }
        default:
            break;
    }
}

export function handleThreadSubscriptionChange ({ threads, threadId, isSubscribed }) {
    const thread = threads.get(threadId);
    if (!thread) return;

    threads.set(threadId, {
        ...thread,
        inAppCommentThread: {
            ...thread.inAppCommentThread,
            subscribed: isSubscribed,
        },
    });
}

export function handleThreadRead ({ threads, threadId, lastReadAt }) {
    const thread = threads.get(threadId);
    if (!thread) return;

    threads.set(threadId, {
        ...thread,
        lastReadAt,
    });
}

export function hasUnreadComments (comments, lastReadAt) {
    const currentUserId = getActiveUser()?.id;
    lastReadAt = lastReadAt ?? 0;

    return comments.some((comment) => comment.createdAt > lastReadAt && comment.createdByUser?.id !== currentUserId);
}

export function buildLinkToThread (thread, route, toPathFn) {
    const { entities, id: threadId } = thread.inAppCommentThread;
    const { name, query } = route;
    let linkPath;
    if (entities?.space) {
        const spaceId = entities.space[0];
        const nodeId = entities['space-frame']?.[0] ?? entities['space-item']?.[0];
        linkPath = toPathFn({
            name: 'Space',
            params: { id: spaceId },
            query: {
                ...(nodeId ? { nodeId } : {}),
                openThread: true,
            },
        });
    }
    if (entities?.dashboard) {
        linkPath = toPathFn({
            name,
            params: {
                id: entities.dashboard[0],
            },
            query: {
                widgetId: entities.widget?.[0],
                threadId,
            },
        });
    }
    if (entities?.feedback || entities?.idea) {
        linkPath = toPathFn({
            name,
            query: {
                openItem: query?.openItem,
                threadId,
            },
        });
    }

    if (!linkPath) return null;

    return `${window.location.origin}${linkPath}`;
}

export function getGroupedThreads (threads, totalTime) {
    const GROUPING_THRESHOLD_PERCENT = 2;
    const sorted = [...threads].sort(
        (a, b) =>
            a.inAppCommentThread.contextJson.currentMediaPosition -
            b.inAppCommentThread.contextJson.currentMediaPosition,
    );

    if (!sorted.length) return [];

    const threshold = (GROUPING_THRESHOLD_PERCENT / 100) * totalTime;
    const groups = [];
    let currentGroup = [sorted[0]];
    let groupStart = sorted[0].inAppCommentThread.contextJson.currentMediaPosition;

    for (let i = 1; i < sorted.length; i++) {
        const pos = sorted[i].inAppCommentThread.contextJson.currentMediaPosition;
        if (pos - groupStart <= threshold) {
            currentGroup.push(sorted[i]);
        } else {
            groups.push(currentGroup);
            currentGroup = [sorted[i]];
            groupStart = pos;
        }
    }
    groups.push(currentGroup);

    return groups.map((group) =>
        [...group].sort(
            (a, b) => a.inAppCommentThread.createdAt - b.inAppCommentThread.createdAt,
        ),
    );
}
